import React, { useEffect, useState, useRef, useCallback } from "react";
import { IconButton, Box, Slider, Stack } from "@mui/material";
import { PlayArrow, VolumeUp, VolumeOff } from "@mui/icons-material";
import { Col, Row } from "react-bootstrap";
import PauseIcon from "@mui/icons-material/Pause";

export default function AudioPlayer(props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  // const [tabHasFocus, setTabHasFocus] = useState(true); // useState(false);
  const tabHasFocus = true;
  const [audioTime, setAudioTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  const audioRef = useRef(null);

  // Create and configure Audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }

    const audio = new Audio(props.audio);
    audioRef.current = audio;
    setAudioTime(0);
    setAudioDuration(0);

    // Apply loop, volume immediately before playback starts
    audio.loop = props.audioObject?.loop || false;
    audio.volume = props.audioObject?.customVolume
      ? props.audioObject?.volumeValue ?? 1.0
      : 1.0;

    // Attach all listeners directly — no refs, no split effects
    const onTimeUpdate = () => {
      setAudioTime(audio.currentTime);
      const dur = audio.duration;
      if (dur && isFinite(dur)) {
        setAudioDuration(dur);
      }
    };

    const onDurationChange = () => {
      const dur = audio.duration;
      if (dur && isFinite(dur)) {
        setAudioDuration(dur);
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("ended", onEnded);

    // If metadata already loaded (e.g. cached), set duration now
    if (audio.duration && isFinite(audio.duration)) {
      setAudioDuration(audio.duration);
    }

    if (tabHasFocus && props.audioObject?.autoPlay && !props.promptVisible) {
      audio.play().catch((e) => console.error("Audio play failed:", e));
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("ended", onEnded);
      audio.src = "";
    };
  }, [props.audio, props.audioObject?.autoPlay, props.promptVisible, tabHasFocus]);

  useEffect(() => {
    if (!props.editMode) {
      if (tabHasFocus && props.audioObject?.autoPlay && !props.promptVisible) {
        if (!isPlaying) {
          togglePlay();
        }
      }
      if (isPlaying && !tabHasFocus) {
        audioRef.current.pause();
        setIsPlaying(!isPlaying);
        // stopAudio();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.audioTrigger, tabHasFocus, props?.promptVisible]);

  // reveal controls override
  useEffect(() => {
    if (props.playOverride !== null) {
      if (isPlaying || props.playOverride) {
        togglePlay();
      }
      if (isPlaying && !tabHasFocus) {
        stopAudio();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.playOverride]);

  // pass audio status
  useEffect(() => {
    props.audioPlaying(isPlaying);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = props?.audioObject?.loop || false;
    }
  }, [props?.audioObject?.loop]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = props?.audioObject?.customVolume
        ? props?.audioObject?.volumeValue ?? 1.0
        : 1.0;
    }
  }, [props?.audioObject, props.audio]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else if (tabHasFocus) {
      audioRef.current.play().catch((e) => console.error("Play failed:", e));
    }
    setIsPlaying(!isPlaying);
  });

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  const handleMouseEnter = () => {
    if (props.disableDraggable) {
      props.disableDraggable(true);
    }
  };

  const handleMouseLeave = () => {
    if (props.disableDraggable) {
      props.disableDraggable(false);
    }
  };

  const player = () => {
    return (
      <Stack
        className="audio-player"
        direction="row"
        justifyContent="start"
        sx={{
          width: "200px",
        }}
      >
        <Row className="d-flex w-100 m-0 justify-content-center">
          <Col className={`player-section player p-0`}>
            <Row>
              <Col xs="auto" className="part btn">
                <IconButton
                  className="play-btn control-btn"
                  onClick={() => {
                    togglePlay();
                  }}
                >
                  {" "}
                  {isPlaying ? <PauseIcon /> : <PlayArrow />}
                </IconButton>
              </Col>
              <Col
                className="part track"
                onMouseEnter={() => handleMouseEnter()}
                onMouseLeave={() => handleMouseLeave()}
              >
                <Slider
                  className="control-slider"
                  size="small"
                  min={0}
                  step={0.1}
                  value={audioTime}
                  max={audioDuration || 0}
                  onChange={(event, value) => {
                    setAudioTime(value);
                    if (audioRef.current) {
                      audioRef.current.currentTime = value;
                    }
                  }}
                />
              </Col>
              <Col xs="auto" className="part btn">
                <IconButton
                  className="mute-btn control-btn"
                  onClick={() => {
                    setIsMuted(!isMuted);
                  }}
                >
                  {isMuted ? <VolumeOff /> : <VolumeUp />}
                </IconButton>
              </Col>
            </Row>
          </Col>
        </Row>
      </Stack>
    );
  };

  return (
    <>
      <Box className="player-container">{player()}</Box>
    </>
  );
}
